import React, { Dispatch, SetStateAction, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid2,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LuListTodo, LuX } from "react-icons/lu";
import ModalBase from "../../../components/common/modal/ModalBase";
import { cloneDeep } from "lodash";
import { v4 } from "uuid";

type FieldType = { value: string; label: string };
interface ISearch {
  handleSearch?: (tags: string[]) => void;
  originFields?: FieldType[];
  fields?: FieldType[];
  tags?: string[];
  loading?: boolean;
  setTags?: Dispatch<SetStateAction<string[]>>;
  setFields?: Dispatch<SetStateAction<FieldType[]>>;
}

function Search({
  originFields,
  fields,
  tags,
  loading,
  handleSearch,
  setFields,
  setTags,
}: ISearch) {
  const [text, setText] = useState<string>("");
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [allowAddTag, setAllowAddTag] = useState<boolean>(true);
  const [allowDeleteTag, setAllowDeleteTag] = useState<boolean>(true);
  const [anchorMenu, setAnchorMenu] = useState<HTMLButtonElement | null>(null);

  const handleCloseModal = () => setOpenModal(false);

  const handleCloseMenu = () => setAnchorMenu(null);

  const handleChangeField = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    const value = e.target.value;
    let fieldsClone = cloneDeep(fields || []);
    if (checked) {
      const fieldToAdd = (originFields || []).find((f) => f.value === value);
      if (fieldToAdd) {
        fieldsClone?.push(fieldToAdd);
      }
    } else {
      fieldsClone = (fields || []).filter((f) => f.value !== value);
    }
    setFields?.(fieldsClone);
  };

  const handleSaveTag = () => {
    if (!text || !allowAddTag) return;
    setAllowAddTag(false);
    const newTags = [...(tags || []), text];
    setTags?.(newTags);
    setText("");
    handleSearch?.(newTags);
    setTimeout(() => {
      setAllowAddTag(true);
    }, 1000);
  };

  const handleDeleteTag = (index: number) => {
    if (!allowDeleteTag || !!loading) return;
    setAllowDeleteTag(false);
    const tagsClone = cloneDeep(tags || []);
    tagsClone?.splice(index, 1);
    setTags?.(tagsClone);
    handleSearch?.(tagsClone);
    if (tagsClone.length <= 2) {
      setAnchorMenu(null);
    }
    setTimeout(() => {
      setAllowDeleteTag(true);
    }, 1000);
  };

  // const handleDeleteTagByBackspace = () => {
  //   if (!allowDeleteTag) return;
  //   setAllowDelete(false);
  //   const tagsClone = cloneDeep(tags || []);
  //   tagsClone.pop();
  //   setTags?.(tagsClone);
  //   handleSearch?.(tagsClone);
  //   setTimeout(() => {
  //     setAllowDelete(true);
  //   }, 500);
  // };

  const showOption = !!(originFields && originFields.length > 0);
  const moreTags = cloneDeep(tags || []);
  moreTags.pop();

  return (
    <>
      {showOption && (
        <ModalBase
          open={openModal}
          handleClose={handleCloseModal}
          width="800px"
          title="Tùy chỉnh các trường tìm kiếm"
          hideCloseIcon
          actions={[
            <Button
              key={1}
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                handleCloseModal();
                handleSearch?.(tags || []);
              }}
              sx={{
                textTransform: "none",
                color: "common.white",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                },
              }}
            >
              Áp dụng
            </Button>,
            <Button
              key={2}
              variant="contained"
              color="error"
              size="small"
              onClick={handleCloseModal}
              sx={{
                textTransform: "none",
                color: "common.white",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                },
              }}
            >
              Đóng
            </Button>,
          ]}
        >
          <Box sx={{ px: 2 }}>
            <Grid2 container spacing={2}>
              {originFields.map((field) => {
                const checked = (fields || [])?.find(
                  (f) => f.value === field.value
                );
                return (
                  <Grid2 key={field.value} size={{ xs: 6, md: 3 }}>
                    <FormControlLabel
                      label={field.label}
                      control={
                        <Checkbox
                          size="small"
                          value={field.value}
                          checked={!!checked}
                          onChange={handleChangeField}
                        />
                      }
                    />
                  </Grid2>
                );
              })}
            </Grid2>
          </Box>
        </ModalBase>
      )}
      {/* Menu tags */}
      {moreTags?.length >= 2 && (
        <Menu
          open={!!anchorMenu}
          anchorEl={anchorMenu}
          onClose={handleCloseMenu}
          slotProps={{
            paper: {
              elevation: 1,
              sx: { width: "150px" },
            },
          }}
        >
          {(moreTags || []).map((tag, index) => {
            return (
              <MenuItem key={v4()}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    width: "100%",
                    "&:hover": {
                      "& .btn-delete-tag": { visibility: "visible" },
                    },
                  }}
                >
                  <Typography>{tag}</Typography>
                  <IconButton
                    onClick={() => handleDeleteTag(index)}
                    className="btn-delete-tag"
                    sx={{ visibility: "hidden", transition: "all linear 0.1s" }}
                  >
                    <LuX size={16} />
                  </IconButton>
                </Stack>
              </MenuItem>
            );
          })}
        </Menu>
      )}
      {/* Search input */}
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          border: "2px solid",
          borderColor: "divider",
          borderRadius: "10px",
          px: 1,
          "&:focus-within": {
            borderColor: "primary.main",
          },
        }}
      >
        <Stack
          sx={{ flex: 1 }}
          direction="row"
          alignItems="center"
          flexWrap="nowrap"
        >
          <Stack direction="row" gap={0.2} alignItems="center">
            {tags && tags.length > 0 && (
              <>
                {tags.length > 2 ? (
                  <>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={(e) => setAnchorMenu(e.currentTarget)}
                      sx={{
                        p: 0,
                        fontWeight: 400,
                        fontSize: "13px",
                        textTransform: "none",
                        width: "120px",
                        borderRadius: "4px",
                        boxShadow: "none",
                        "&:hover": {
                          boxShadow: "none",
                        },
                      }}
                    >
                      {tags.length - 1} từ khóa khác
                    </Button>
                    <Chip
                      key={v4()}
                      label={tags[tags.length - 1]}
                      size="small"
                      onDelete={() => handleDeleteTag(tags.length - 1)}
                      sx={{ borderRadius: "4px", maxWidth: "100px" }}
                    />
                  </>
                ) : (
                  <>
                    {tags.map((tag, index) => {
                      return (
                        <Chip
                          key={v4()}
                          label={tag}
                          size="small"
                          onDelete={() => handleDeleteTag(index)}
                          sx={{ borderRadius: "4px", maxWidth: "100px" }}
                        />
                      );
                    })}
                  </>
                )}
              </>
            )}
          </Stack>
          <TextField
            fullWidth
            variant="outlined"
            autoComplete="off"
            spellCheck="false"
            placeholder="Tìm kiếm nhanh"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyUp={(e) => {
              if (e.which === 13) {
                handleSaveTag();
              }
              // if (e.which === 8 && !text && (tags || []).length > 0) {
              //   handleDeleteTagByBackspace();
              // }
            }}
            sx={{
              "& .MuiInputBase-root": {
                px: 1,
                "& .MuiInputBase-input": {
                  px: 0,
                  py: 1,
                  height: "34px",
                },
                "& fieldset": {
                  border: "none",
                },
              },
            }}
            // slotProps={{
            //   input: {
            //     startAdornment: (
            //       <IconButton onClick={handleSearch}>
            //         <LuSearch size={20} />
            //       </IconButton>
            //     ),
            //     endAdornment: showOption ? (
            //       <IconButton
            //         onClick={() => setOpenModal(true)}
            //         size="small"
            //         sx={{
            //           color: "secondary.main",
            //           borderRadius: "10px",
            //           backgroundColor: "secondary.600",
            //           "&:hover": {
            //             backgroundColor: "secondary.600",
            //           },
            //         }}
            //       >
            //         <LuListTodo size={20} />
            //       </IconButton>
            //     ) : null,
            //   },
            // }}
          />
        </Stack>
        <IconButton
          onClick={() => setOpenModal(true)}
          size="small"
          sx={{
            color: "secondary.main",
            borderRadius: "10px",
            backgroundColor: "secondary.600",
            "&:hover": {
              backgroundColor: "secondary.600",
            },
          }}
        >
          <LuListTodo size={20} />
        </IconButton>
      </Stack>
    </>
  );
}

export default Search;
